import { isLocalhost } from "./environment";

const UTM_STORAGE_KEY = "egodziny_utm_params";

export const captureUtmParams = () => {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const utm = {
    utm_source: params.get("utm_source"),
    utm_medium: params.get("utm_medium"),
    utm_campaign: params.get("utm_campaign"),
    utm_content: params.get("utm_content"),
    utm_term: params.get("utm_term"),
  };
  const hasUtm = Object.values(utm).some(Boolean);

  try {
    const existing = JSON.parse(localStorage.getItem(UTM_STORAGE_KEY) || "null");
    const updated = { ...(existing || {}) };

    if (hasUtm) {
      Object.assign(updated, utm, {
        capturedAt: new Date().toISOString(),
        capturedPath: window.location.pathname,
      });
    }

    // Save landing referrer only once — first visit, never overwrite
    if (!updated.landingReferrer && !updated.landingPath) {
      updated.landingReferrer = document.referrer || null;
      updated.landingPath = window.location.pathname;
      updated.landingAt = new Date().toISOString();
    }

    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(updated));
  } catch {}

  return hasUtm ? utm : null;
};

export const getStoredUtmParams = () => {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

// Prefer GA-specific ID when provided, otherwise reuse Firebase Measurement ID (works with GA4)
const measurementId =
  process.env.REACT_APP_GA_MEASUREMENT_ID ||
  process.env.REACT_APP_FIREBASE_MEASUREMENT_ID;

let initialized = false;
let lastTrackedPath = null;

const createGtag = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };
};

export const initAnalytics = () => {
  if (initialized) return measurementId;
  if (isLocalhost()) {
    console.info("Skipping GA init on localhost.");
    return null;
  }
  if (!measurementId || !measurementId.trim()) {
    console.warn("Google Analytics measurement ID is not set.");
    return null;
  }

  createGtag();

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.gtag("js", new Date());
  window.gtag("config", measurementId, { send_page_view: false });

  initialized = true;
  return measurementId;
};

export const trackPageView = (path) => {
  if (!initialized || !path) return;
  if (path === lastTrackedPath) return;

  window.gtag("event", "page_view", {
    page_path: path,
  });

  lastTrackedPath = path;
};

export const trackEvent = (eventName, eventParams = {}) => {
  if (!initialized || !eventName) return;

  window.gtag("event", eventName, {
    ...eventParams,
  });
};

export const trackRegisterClick = (source) => {
  trackEvent("register_click", {
    source: source || "unknown",
    timestamp: new Date().toISOString(),
  });
};

export const trackRegistrationStart = (context = {}) => {
  trackEvent("registration_start", {
    timestamp: new Date().toISOString(),
    ...context,
  });
};

export const trackRegistrationStep = (stepName, context = {}) => {
  trackEvent("registration_step", {
    step: stepName,
    timestamp: new Date().toISOString(),
    ...context,
  });
};

export const trackRegistrationComplete = (context = {}) => {
  trackEvent("registration_complete", {
    timestamp: new Date().toISOString(),
    ...context,
  });
};

export const trackRegistrationError = (context = {}) => {
  trackEvent("registration_error", {
    timestamp: new Date().toISOString(),
    ...context,
  });
};
